/**
 * Stone Investment Project Detail Hero
 *
 * Cinematic hero for project detail pages built on StoneCinematicHeroCarousel.
 * Displays project name, tagline, location, status, and key stats over a
 * multi-image carousel with Ken Burns zoom and crossfade transitions.
 */

'use client';

import { MapPin } from 'lucide-react';
import { detectRegion } from '../utils/regionColors';
import { StoneCinematicHeroCarousel, type CinematicHeroImage } from './StoneCinematicHeroCarousel';

export interface StoneProjectDetailHeroProps {
  name: string;
  tagline?: string | undefined;
  location?: string | undefined;
  images: CinematicHeroImage[];
  status?: 'PLANNING' | 'UNDER_CONSTRUCTION' | 'COMPLETED' | 'SELLING' | undefined;
  completionDate?: string | undefined;
  totalUnits?: number | undefined;
  messages?: Record<string, unknown>;
  className?: string;
}

function getStatusInfo(status?: string, labels?: Record<string, string>): { label: string; className: string } {
  switch (status) {
    case 'PLANNING':
      return { label: labels?.inPlanning || 'In Planning', className: 'bg-stone-600 text-white' };
    case 'UNDER_CONSTRUCTION':
      return { label: labels?.underConstruction || 'Under Construction', className: 'bg-stone-800 text-white' };
    case 'COMPLETED':
      return { label: labels?.completed || 'Completed', className: 'bg-stone-900 text-white' };
    case 'SELLING':
      return { label: labels?.nowSelling || 'Now Selling', className: 'bg-stone-700 text-white' };
    default:
      return { label: '', className: '' };
  }
}

export function StoneProjectDetailHero({
  name,
  tagline,
  location,
  images,
  status,
  completionDate,
  totalUnits,
  messages,
  className = '',
}: StoneProjectDetailHeroProps) {
  const region = detectRegion(location || name);
  const isFrance = region === 'provence';
  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const heroLabels = (stoneMessages.projectHero || {}) as Record<string, string>;
  const statusInfo = getStatusInfo(status, heroLabels);

  return (
    <section className={`relative isolate ${className}`}>
      <StoneCinematicHeroCarousel images={images}>
        {/* Status Badge */}
        {statusInfo.label && (
          <div
            className={`absolute top-6 left-6 px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-sans z-30 ${statusInfo.className}`}
          >
            {statusInfo.label}
          </div>
        )}

        {/* Content Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 z-30">
          <div className="container mx-auto max-w-7xl">
            {location && (
              <div className="flex items-center gap-2 mb-4">
                <MapPin size={14} className="text-stone-300" strokeWidth={1.5} />
                <span className="text-xs uppercase tracking-[0.2em] text-stone-300 font-sans">
                  {location}
                </span>
              </div>
            )}

            <h1
              className="font-serif text-4xl md:text-6xl lg:text-7xl text-white mb-4 leading-[1.1]"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              {name}
            </h1>

            {tagline && (
              <p className="font-serif text-xl md:text-2xl text-stone-200 italic mb-6 max-w-2xl">
                {tagline}
              </p>
            )}

            <div className="flex flex-wrap gap-8 pt-6 border-t border-white/20">
              {totalUnits && (
                <div>
                  <span className="font-serif text-3xl text-white">
                    {totalUnits}
                  </span>
                  <span className="text-xs uppercase tracking-[0.2em] text-stone-300 font-sans ml-2">
                    {heroLabels.units || 'Units'}
                  </span>
                </div>
              )}
              {completionDate && (
                <div>
                  <span className="font-serif text-3xl text-white">
                    {completionDate}
                  </span>
                  <span className="text-xs uppercase tracking-[0.2em] text-stone-300 font-sans ml-2">
                    {heroLabels.completion || 'Completion'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </StoneCinematicHeroCarousel>

      {/* Flag Colour Strip */}
      <div className="absolute bottom-0 left-0 right-0 h-1 flex z-40">
        {isFrance ? (
          <>
            <div className="h-full flex-1 bg-[#002654]" />
            <div className="h-full flex-1 bg-white" />
            <div className="h-full flex-1 bg-[#ED2939]" />
          </>
        ) : (
          <>
            <div className="h-full flex-1 bg-[#EA2839]" />
            <div className="h-full flex-1 bg-[#1A206D]" />
            <div className="h-full flex-1 bg-[#FFD500]" />
            <div className="h-full flex-1 bg-[#00A551]" />
          </>
        )}
      </div>
    </section>
  );
}

export default StoneProjectDetailHero;
